import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import {
  createShare,
  listDocumentShares,
  listShares,
  revokeShare,
  shareDocument,
  updateShareRole,
  type ClipboardShare,
} from "@/features/clipboard/api/clipboard";
import { Trash2, Users } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useId, useState } from "react";

interface SharePanelProps {
  /** Folder share target (mutually exclusive with documentId). */
  folderPath?: string;
  /** Document share target (mutually exclusive with folderPath). */
  documentId?: string;
  /** Display name for the document being shared. */
  documentName?: string;
  /** Replaces the folder or file name in the title. */
  title?: string;
  /** Shown above the invite form. */
  intro?: ReactNode;
  /** Shown below the list of people. */
  footer?: ReactNode;
  onClose: () => void;
}

export const SharePanel = ({
  folderPath,
  documentId,
  documentName,
  title,
  intro,
  footer,
  onClose,
}: SharePanelProps) => {
  const isDocument = !!documentId;
  const targetLabel = title || (isDocument ? documentName || "this file" : folderPath || "");
  const emailId = useId().replace(/:/g, "");
  const [shares, setShares] = useState<ClipboardShare[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"viewer" | "editor">("viewer");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = documentId
        ? await listDocumentShares(documentId)
        : await listShares(folderPath ?? "");
      setShares(data);
    } catch {
      setError("Failed to load shares");
    } finally {
      setLoading(false);
    }
  }, [documentId, folderPath]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError(null);
    setSubmitting(true);
    try {
      if (documentId) {
        await shareDocument(documentId, email.trim(), role);
      } else {
        await createShare(folderPath ?? "", email.trim(), role);
      }
      setEmail("");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to share");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (shareId: string) => {
    try {
      await revokeShare(shareId);
      setShares((prev) => prev.filter((s) => s.id !== shareId));
    } catch {
      setError("Failed to remove share");
    }
  };

  const handleRoleChange = async (shareId: string, newRole: "viewer" | "editor") => {
    try {
      await updateShareRole(shareId, newRole);
      setShares((prev) => prev.map((s) => (s.id === shareId ? { ...s, role: newRole } : s)));
    } catch {
      setError("Failed to update role");
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md gap-0 p-0">
        <DialogHeader className="space-y-0 border-b border-zinc-100 px-5 py-4 text-left">
          <DialogTitle className="flex min-w-0 items-center gap-2 text-base">
            <Users className="h-5 w-5 shrink-0 text-zinc-500" aria-hidden />
            <span className="min-w-0 truncate" title={targetLabel}>
              Share &ldquo;{targetLabel}&rdquo;
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Invite people by email and manage who can view or edit this{" "}
            {isDocument ? "file" : "folder"}.
          </DialogDescription>
        </DialogHeader>

        <div className="p-5">
          {intro}
          <form onSubmit={handleInvite} className="mb-4 flex gap-2">
            <label htmlFor={emailId} className="sr-only">
              Email address
            </label>
            <Input
              id={emailId}
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 border-zinc-200 focus:ring-zinc-900/10"
              disabled={submitting}
              autoComplete="email"
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as "viewer" | "editor")}
              aria-label="Role"
              className="h-8 rounded-[3px] border border-zinc-200 bg-white px-2 text-base text-zinc-600 focus:outline-none focus:ring-2 focus:ring-zinc-900"
              disabled={submitting}
            >
              <option value="viewer">Viewer</option>
              <option value="editor">Editor</option>
            </select>
            <Button
              type="submit"
              disabled={submitting || !email.trim()}
              loading={submitting}
              className="h-8 px-4"
            >
              Invite
            </Button>
          </form>

          {error && (
            <p role="alert" className="mb-3 text-sm text-red-700">
              {error}
            </p>
          )}

          <div className="space-y-1">
            {loading ? (
              <p className="py-4 text-center text-sm text-zinc-500">Loading...</p>
            ) : shares.length === 0 ? (
              <p className="py-4 text-center text-sm text-zinc-500">
                {isDocument
                  ? "This file is not shared with anyone"
                  : "This folder is not shared with anyone"}
              </p>
            ) : (
              shares.map((share) => (
                <div
                  key={share.id}
                  className="flex items-center justify-between rounded-[3px] px-2 py-2 hover:bg-zinc-50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-zinc-800">
                      {share.shared_with_name || share.shared_with_email}
                    </p>
                    {share.shared_with_name && (
                      <p className="truncate text-xs text-zinc-500">
                        {share.shared_with_email}
                      </p>
                    )}
                  </div>
                  <div className="ml-3 flex items-center gap-2">
                    <select
                      value={share.role}
                      onChange={(e) =>
                        handleRoleChange(share.id, e.target.value as "viewer" | "editor")
                      }
                      aria-label={`Role for ${share.shared_with_email}`}
                      className="h-7 rounded border border-zinc-200 bg-white px-1.5 text-base text-zinc-600"
                    >
                      <option value="viewer">Viewer</option>
                      <option value="editor">Editor</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => handleRevoke(share.id)}
                      className="rounded p-1 text-zinc-400 transition-colors hover:bg-red-50 hover:text-red-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900"
                      aria-label="Remove access"
                      title="Remove access"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          {footer}
        </div>
      </DialogContent>
    </Dialog>
  );
};
